import { Link } from "react-router";

import { Avatar } from "../ui/avatar";
import { Button } from "../ui/button";
import { cn } from "../ui/cn";
import { Icon } from "../ui/icon";
import { firstNameOf } from "./greeting";
import type { WaitingModel } from "./needs-you";

// The dark card of Home: how many customers wait on a person, who they are, and a button to the most urgent one. It is drawn with palette
// colours only (the page text colour as the fill, the page ground as the text); the Paper mock-up's white-with-alpha text is the border grey.

const countText = (model: Extract<WaitingModel, { state: "ready" }>): string =>
  `${model.count}${model.capped ? "+" : ""}`;

/** One plain sentence for the page header: how many customers wait on a person. `null` when the role has no such queue. */
export function waitingSentence(model: WaitingModel): string | null {
  if (model.state === "hidden") return null;
  if (model.state === "blind")
    return "The queue of customers who wait on a person could not be read.";
  if (model.count === 0) return "Nobody waits on a person.";
  const noun = model.count === 1 && !model.capped ? "customer waits" : "customers wait";
  return `${countText(model)} ${noun} on a person.`;
}

function Figure({ model }: Readonly<{ model: WaitingModel }>) {
  if (model.state === "blind") {
    return <p className="m-0 text-lg leading-7 text-border">This queue could not be read.</p>;
  }
  if (model.state === "hidden") return null;
  if (model.count === 0) {
    return <p className="m-0 text-lg leading-7 text-border">Nobody waits on a person.</p>;
  }
  return (
    <p className="m-0 flex items-end gap-3.5">
      <span className="font-serif text-[84px] leading-[76px] font-medium tracking-[-0.03em]">
        {countText(model)}
      </span>
      <span className="pb-1.5 text-base leading-[22px] text-border">
        {model.count === 1 && !model.capped ? "conversation" : "conversations"}
        {model.oldestMinutes === null ? "" : ` · oldest ${model.oldestMinutes} min`}
      </span>
    </p>
  );
}

function Who({ model, startHref }: Readonly<{ model: WaitingModel; startHref: string | null }>) {
  if (model.state !== "ready") return null;
  const first = model.start === null ? null : firstNameOf(model.start.item.customer.displayName);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex gap-1 compact:gap-2" aria-hidden="true">
        {model.customers.map((customer) => (
          <Avatar key={customer.id} id={customer.id} name={customer.displayName} size="sm" />
        ))}
      </div>
      {startHref !== null && first !== null ? (
        <Button asChild variant="outline" className="border-transparent">
          <Link to={startHref}>
            Start with {first}
            <Icon name="arrow-right-02-round" size="md" />
          </Link>
        </Button>
      ) : null}
    </div>
  );
}

type HomeHeroProps = Readonly<{
  model: Exclude<WaitingModel, { state: "hidden" }>;
  /** The address of the most urgent conversation, or `null` while the Concierge area does not open for the role. */
  startHref: string | null;
  /** `true`: no cards stand beside it, so the card spans the row and puts the people and the button on the right. */
  wide: boolean;
}>;

export function HomeHero({ model, startHref, wide }: HomeHeroProps) {
  return (
    <section
      aria-labelledby="home-waiting"
      className={cn(
        "flex min-h-[200px] flex-col justify-between gap-4 rounded-lg bg-foreground px-5 py-5 text-background sm:px-[22px]",
        wide && "lg:min-h-0 lg:flex-row lg:items-center",
      )}
    >
      <div className="grid gap-3">
        <h2
          id="home-waiting"
          className="m-0 text-meta font-semibold tracking-[0.06em] text-border uppercase"
        >
          Waiting on a person
        </h2>
        <Figure model={model} />
      </div>
      <div className={cn(wide && "lg:min-w-80")}>
        <Who model={model} startHref={startHref} />
      </div>
    </section>
  );
}
